import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { X, ChevronRight, ChevronLeft, Sparkles, Rocket, MapPin, Users, Calendar, DollarSign, Vote, Car, FileText, HelpCircle } from 'lucide-react';

// Tutorial steps for regular trips (with dates)
const TRIP_TUTORIAL_STEPS = [
  {
    id: 'welcome_trip',
    title: 'Sua viagem foi criada! 🎉',
    description: 'Parabéns! Agora vamos configurar tudo para uma viagem incrível.',
    icon: '✈️',
  },
  {
    id: 'improve_description',
    title: 'Melhore a descrição',
    description: 'Adicione uma descrição detalhada, escolha uma imagem de capa e defina o orçamento estimado para ajudar os participantes.',
    icon: '📝',
    tip: 'Uma boa descrição ajuda todos a entenderem o objetivo da viagem!',
  },
  {
    id: 'add_itinerary',
    title: 'Monte o roteiro',
    description: 'Já tem atividades planejadas? Adicione cada dia da viagem com horários, locais e custos.',
    icon: '📅',
    tip: 'Dica: Comece pelos itens que já estão confirmados!',
  },
  {
    id: 'invite_members',
    title: 'Convide os amigos',
    description: 'Compartilhe o código da viagem para seus amigos entrarem. Você pode aprovar cada solicitação.',
    icon: '👥',
    tip: 'O código está no topo da página. Copie e envie!',
  },
  {
    id: 'manage_payments',
    title: 'Gerencie pagamentos',
    description: 'Crie despesas comuns e o sistema divide automaticamente entre todos. Acompanhe quem já pagou!',
    icon: '💰',
    tip: 'Pagamentos vinculados ao roteiro são gerados automaticamente.',
  },
  {
    id: 'organize_carpools',
    title: 'Organize caronas',
    description: 'Ofereça ou peça carona. Defina pontos de encontro e divida os custos de combustível.',
    icon: '🚗',
    tip: 'Caronas economizam dinheiro e são mais divertidas!',
  },
  {
    id: 'trip_ready',
    title: 'Tudo pronto! 🚀',
    description: 'Sua viagem está configurada. Explore os menus para descobrir mais funcionalidades!',
    icon: '🎊',
  },
];

// Tutorial steps for voting trips
const VOTING_TUTORIAL_STEPS = [
  {
    id: 'welcome_voting',
    title: 'Votação criada! 🗳️',
    description: 'Ótimo! Agora o grupo pode votar no melhor destino para a viagem.',
    icon: '🗳️',
  },
  {
    id: 'voting_how',
    title: 'Como funciona?',
    description: 'Cada participante pode votar nos destinos sugeridos. O mais votado será escolhido!',
    icon: '🎯',
    tip: 'Você pode adicionar mais opções de destino a qualquer momento.',
  },
  {
    id: 'add_options',
    title: 'Adicione destinos',
    description: 'Sugira novos destinos com datas e descrições. Quanto mais opções, melhor a decisão!',
    icon: '🌎',
    tip: 'Inclua informações como clima, preços e atrações.',
  },
  {
    id: 'invite_voters',
    title: 'Convide para votar',
    description: 'Compartilhe o código da viagem para os amigos entrarem e votarem no destino.',
    icon: '👥',
    tip: 'Mais votos = decisão mais democrática!',
  },
  {
    id: 'select_winner',
    title: 'Escolha o vencedor',
    description: 'Quando a votação terminar, selecione o destino vencedor para transformar em viagem!',
    icon: '🏆',
    tip: 'Você pode encerrar a votação a qualquer momento.',
  },
  {
    id: 'voting_ready',
    title: 'Votação configurada! 🎉',
    description: 'Agora é só aguardar os votos. Boa sorte com a escolha do destino!',
    icon: '🚀',
  },
];

export const TripTutorial = ({ 
  isOpen, 
  onClose, 
  onComplete, 
  tripType = 'regular', // 'regular' or 'voting'
  tripId 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { api } = useAuth();

  const steps = tripType === 'voting' ? VOTING_TUTORIAL_STEPS : TRIP_TUTORIAL_STEPS;
  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = async () => {
    if (isLastStep) {
      await handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      // Save preference to not show again if checked
      if (dontShowAgain) {
        localStorage.setItem(`trip_tutorial_${tripType}_hidden`, 'true');
      }
      // Mark tutorial as completed for this trip
      if (tripId) {
        await api.post(`/trips/${tripId}/tutorial/complete`, {
          tutorial_type: tripType,
          dont_show_again: dontShowAgain
        }).catch(() => {});
      }
    } catch (e) {
      console.log('Error saving tutorial preference:', e);
    }
    onComplete?.();
  };

  const handleSkip = async () => {
    if (dontShowAgain) {
      localStorage.setItem(`trip_tutorial_${tripType}_hidden`, 'true');
    }
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className={`p-6 text-white ${
              tripType === 'voting' 
                ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                : 'bg-gradient-to-r from-orange-500 to-pink-500'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {tripType === 'voting' ? 'Guia de Votação' : 'Guia da Viagem'}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20 h-8 w-8"
                  onClick={handleSkip}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Progress dots */}
              <div className="flex justify-center gap-1.5 mb-4">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentStep
                        ? 'bg-white w-6'
                        : index < currentStep
                        ? 'bg-white/60'
                        : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>

              {/* Icon */}
              <div className="text-6xl text-center mb-2">{step.icon}</div>
            </div>

            <CardContent className="p-6">
              {/* Content */}
              <div className="text-center mb-4">
                <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">{step.description}</p>
              </div>

              {/* Tip */}
              {step.tip && (
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 mb-4">
                  <p className="text-sm text-orange-700 dark:text-orange-300 flex items-start gap-2">
                    <span className="text-lg">💡</span>
                    {step.tip}
                  </p>
                </div>
              )}

              {/* Don't show again checkbox - only on last step */}
              {isLastStep && (
                <div className="flex items-center gap-2 mb-4 justify-center">
                  <Checkbox
                    id="dont-show-again"
                    checked={dontShowAgain}
                    onCheckedChange={setDontShowAgain}
                  />
                  <label 
                    htmlFor="dont-show-again" 
                    className="text-sm text-slate-500 dark:text-slate-400 cursor-pointer"
                  >
                    Não mostrar novamente
                  </label>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                {!isFirstStep && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    className="rounded-full"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Voltar
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  className={`flex-1 rounded-full ${
                    tripType === 'voting'
                      ? 'bg-purple-500 hover:bg-purple-600'
                      : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                  data-testid="trip-tutorial-next-btn"
                >
                  {isLastStep ? (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Começar!
                    </>
                  ) : (
                    <>
                      Próximo
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>

              {/* Skip link */}
              {!isLastStep && (
                <button
                  onClick={handleSkip}
                  className="w-full mt-4 text-sm text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Pular guia
                </button>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook to manage trip tutorial state
export const useTripTutorial = (tripId, tripType = 'regular') => {
  const [showTutorial, setShowTutorial] = useState(false);
  const { api } = useAuth();

  const checkShouldShowTutorial = useCallback(() => {
    // Check localStorage first
    const hidden = localStorage.getItem(`trip_tutorial_${tripType}_hidden`);
    if (hidden === 'true') return false;
    
    // Check if this is a new trip (just created)
    const justCreated = sessionStorage.getItem(`trip_just_created_${tripId}`);
    if (justCreated === 'true') {
      sessionStorage.removeItem(`trip_just_created_${tripId}`);
      return true;
    }
    
    return false;
  }, [tripId, tripType]);

  const openTutorial = () => setShowTutorial(true);
  const closeTutorial = () => setShowTutorial(false);

  useEffect(() => {
    if (tripId && checkShouldShowTutorial()) {
      // Small delay to let page render first
      let isMounted = true;
      const showAfterDelay = async () => {
        await new Promise(resolve => setTimeout(resolve, 500));
        if (isMounted) {
          setShowTutorial(true);
        }
      };
      showAfterDelay();
      return () => { isMounted = false; };
    }
  }, [tripId, checkShouldShowTutorial]);

  return {
    showTutorial,
    openTutorial,
    closeTutorial,
  };
};

// Button to open tutorial from banner
export const TripTutorialButton = ({ onClick, tripType = 'regular' }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors"
      title={tripType === 'voting' ? 'Ver guia da votação' : 'Ver guia da viagem'}
      data-testid="trip-tutorial-button"
    >
      <HelpCircle className="w-4 h-4" />
      <span className="hidden sm:inline">Ajuda</span>
    </button>
  );
};

export default TripTutorial;
